// Foreground mirror of the server push predicate
// `Grappa.Push.Triggers.should_notify?/5` (PWA icon badge, 2026-06-21).
//
// Why a client-side copy exists. The badge's authoritative values come
// from the server (the `/me` seed, the `read_cursor_set` broadcast, and
// the push payload all carry a server-computed count). But the DESKTOP
// `document.title` must also move the instant a notify-worthy message
// arrives in an UNFOCUSED tab — before any read-cursor settle round-trips
// to the server. That single increment needs the same predicate the
// server uses, evaluated locally.
//
// One predicate, two ports. To stop this copy drifting from the Elixir
// original, BOTH run against ONE shared truth-table fixture
// (`shouldNotifyTruthTable.json`): the vitest `pushTriggers.test.ts` and
// the ExUnit `should_notify_parity_test.exs` consume the identical cases.
// Add a branch → add a row → both suites pick it up. Same discipline as
// the wireTypes parity gate.
//
// The mention sub-predicate is NOT reimplemented — it delegates to
// `matchesWatchlist` from `mentionMatch.ts`, the established mirror of
// `Grappa.Mentions.mentioned?/3` (own nick ∪ highlight patterns). #370 —
// the SAME predicate now drives the in-message visual highlight, so the
// notify-match and the visual-match can never diverge again.

import { type MessageKind, NOTIFY_KINDS } from "./api";
import { type ChannelKey, canonicalChannel } from "./channelKey";
import { conversationMuteKey, isConversationMuted } from "./conversationMute";
import { isMentionableSender, isOwnRow, matchesWatchlist } from "./mentionMatch";
import { asciiFold } from "./nickEquals";
import type { NotificationPrefs } from "./userSettings";

// Minimal structural shape the predicate needs — a subset of the wire
// scrollback message. Kept narrow so the truth-table JSON maps directly
// and call sites can pass any message-like object.
export type ShouldNotifyMessage = {
  kind: string;
  channel: string;
  sender: string;
  dm_with?: string | null;
  body: string | null;
};

/**
 * Returns true when `message` should produce a notification for the
 * operator whose IRC nick is `ownNick`, given `prefs` + `patterns`.
 *
 * Faithful transcription of `Grappa.Push.Triggers.should_notify?/5`:
 *   1. kind gate — only the shared `NOTIFY_KINDS` SSOT (privmsg|action,
 *      the "notify" subset of api's CONTENT_KINDS, #395) → everything else
 *      false. NOTICE (services chatter) counts as unread but never notifies.
 *   2. own row (#532 C) — a row this operator authored never notifies.
 *   3. muted conversation (#866, network-keyed by #1038) — the composite
 *      `channelKey(networkSlug, target)`, where target is the channel or the
 *      PEER for a DM, present in `muted_targets`. Beats every reason below
 *      it, mentions included.
 *   4. DM (the row carries `dm_with`, issue 1365): private_messages_all OR
 *      asciiFold(sender) in private_messages_only (mirrors the
 *      server's `canonical_target(sender) in ...`).
 *   5. channel: channel_messages_all OR canonicalChannel(channel) in
 *      channel_messages_only OR (channel_mentions AND mentionable sender
 *      AND mention). #1674 — a service or the server naming you is not a
 *      mention, on the MENTION disjunct only.
 */
export function shouldNotify(
  message: ShouldNotifyMessage,
  networkSlug: string,
  ownNick: string,
  prefs: NotificationPrefs,
  patterns: string[],
): boolean {
  // `message.kind` is a bare string (the truth-table JSON / any message-like
  // object); cast to MessageKind for the typed-set membership check — a
  // non-member string just returns false. Same `.has(x as MessageKind)`
  // convention as `wireNarrow.ts`.
  if (!NOTIFY_KINDS.has(message.kind as MessageKind)) return false;

  // #532 C, #868 — "is this row mine?" asked BEFORE the DM test below,
  // because an OUTBOUND DM carries `dm_with = peer` exactly like an inbound
  // one, and an own channel message would otherwise run the operator's own
  // highlight patterns over the operator's own body. The server has had this step since #532 C
  // (`triggers.ex` `own_row?/2`); this port did not, and the shared fixture
  // had no row that could see the gap.
  //
  // issue 1481 — the predicate itself now lives in `mentionMatch.isOwnRow`,
  // shared with the RENDER port, which was missing the same step. The
  // POSITION stays local to this file: here it gates the whole notification
  // (it outranks the mute and both branches), whereas over there it is one
  // conjunct of the mention rule. Same question, two places to ask it — one
  // spelling, so they cannot answer differently. The `"ascii"` pin and its
  // rationale (#1861) travelled with it.
  if (isOwnRow(message.sender, ownNick)) return false;

  // Mirrors the server's `Message.dm?/1` (issue 1365, protocol 36): a row is
  // a DM when it carries `dm_with`. It used to compare the row's `channel`
  // with the live own nick, but an inbound DM's `channel` is the nick we
  // held AT RECEIPT, and since issue 1365 (ruling relayed from vjt) a nick
  // change rewrites nothing — so that TAG goes stale on our first rename.
  // `!= null` and not `!== null`: a pre-36 server omits the key.
  const isDm = message.dm_with != null;

  // #866 — the per-conversation mute, and it wins over EVERY reason below,
  // including a direct mention (vjt's Q2: the mute always wins, because the
  // polite default for "I silenced this room" is that it stays silent).
  // That is why it sits here and not inside the two branches.
  if (isConversationMuted(prefs.muted_targets, conversationKey(message, networkSlug, isDm)))
    return false;

  if (isDm) {
    return dmMatch(message, prefs);
  }
  return channelMatch(message, prefs, ownNick, patterns);
}

// Which CONVERSATION this row belongs to, in mute terms: the NETWORK it
// arrived on plus the channel for a channel row, or the PEER for a DM — never
// `message.channel`, which an inbound DM sets to own_nick (so keying on it
// would collapse every DM onto one mute). The key builder and the membership
// test both live in `conversationMute.ts` — shared with the #1018 next-active
// cycle and the settings picker so the three can never disagree on a key.
//
// #1038 — the network entered the key on both stacks at once. `networkSlug`
// is a required parameter of `shouldNotify` rather than something read off
// the message, because a scrollback row does not carry its network: the
// listener that receives it does (`subscribe.ts` already has `slug` in scope
// for `effectivelyFocused`).
//
// `until` is deliberately not read on this path: expiry belongs to the
// READER, which is what keeps this predicate pure and the shared
// truth-table free of a `now` column (#866 Q3).
function conversationKey(
  message: ShouldNotifyMessage,
  networkSlug: string,
  isDm: boolean,
): ChannelKey {
  return conversationMuteKey(networkSlug, isDm ? message.sender : message.channel);
}

function dmMatch(message: ShouldNotifyMessage, prefs: NotificationPrefs): boolean {
  // ASCII fold on the sender, mirroring the server's
  // `Identifier.canonical_nick(sender) in private_messages_only` — the
  // whitelist entries are stored server-folded (CASEMAPPING=ascii, A-Z
  // only; #121/#525). A bare `.toLowerCase()` would Unicode-over-fold a
  // non-ASCII nick (`CAFÉ`→`café`), forking the key; neither fold touches
  // `[ ] \ ~`, so `foo[1]`/`foo{1}` stay DISTINCT the way the server keeps them.
  return (
    prefs.private_messages_all || prefs.private_messages_only.includes(asciiFold(message.sender))
  );
}

function channelMatch(
  message: ShouldNotifyMessage,
  prefs: NotificationPrefs,
  ownNick: string,
  patterns: string[],
): boolean {
  return (
    // canonicalChannel (the plain ASCII fold — #537 retired the sigil gate
    // on both ports), NOT a bare toLowerCase, mirroring the server's
    // `Identifier.canonical_target(channel) in
    // channel_messages_only` — the whitelist is stored channel-folded
    // (CASEMAPPING=ascii, A-Z only; #525). bahamut folds `A-Z` ONLY, so
    // `#Chan`/`#chan` are ONE channel but `#chan[1]` and `#chan{1}` are
    // DISTINCT (brackets untouched). A bare toLowerCase is avoided
    // because it Unicode-over-folds non-ASCII (`#CAFÉ`→`#café`), not the
    // brackets.
    prefs.channel_messages_all ||
    prefs.channel_messages_only.includes(canonicalChannel(message.channel)) ||
    // #1674 — the sender half of the mention rule, mirroring the server's
    // `mention_match?/4`. Only the MENTION disjunct carries it: asking for
    // ALL channel messages, or whitelisting a window, is an explicit request
    // that a service reply still honours. A mention is not.
    (prefs.channel_mentions &&
      isMentionableSender(message.sender) &&
      matchesWatchlist(message.body, ownNick, patterns))
  );
}
